import { NEW_MASCOTS } from '@daymade/common-lib/constants';
import clsx from 'clsx';
import HeaderOrderSummary from 'components/enter2026/HeaderOrderSummary';
import ImageWithFallback from 'components/ImageWithFallback';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useHeaderContent } from 'hooks/ui/useHeaderContent';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  ASSET_PREFIX,
  useChristmasLogos,
  useHalloweenLogos,
} from 'utils/constants';
import Chevron from '../../svg/24x24/ChevronRight.svg';
import AnnouncementBanners from '../header/AnnouncementBanners';
import {
  actionItem,
  breakItem,
  isActionItem,
  isBreakItem,
  isLinkItem,
  isSubpagesItem,
  linkItem,
  loggedInMenu,
  loggedOutMenu,
  NAVIGATION_CSS_TRANSITION_MILLIS,
  subpageItem,
} from './constants';
import styles from './header2026.module.css';
import MenuAnnouncement from './menu-announcement';

const BurgerMenuComponent = ({
  menuOpen,
  isLoggedIn,
  toggleNavigation,
  claimablePrizesCount,
}: {
  menuOpen: boolean;
  isLoggedIn: boolean;
  toggleNavigation: () => void;
  claimablePrizesCount?: number;
}) => (
  <div
    className={clsx(styles.burgerMenu, !isLoggedIn && styles.burgerLoggedOut)}
  >
    <button
      type="button"
      aria-label={menuOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={menuOpen}
      className={clsx(styles.menuAction, menuOpen ? styles.burgerOpen : '')}
      onClick={toggleNavigation}
    >
      <span className={clsx(styles.menuBar, styles.menuBarTop)}></span>
      <span className={clsx(styles.menuBar, styles.menuBarMiddle)}></span>
      <span className={clsx(styles.menuBar, styles.menuBarBottom)}></span>
      {claimablePrizesCount && claimablePrizesCount > 0 ? (
        <div
          className={clsx(styles.notifyDot, menuOpen ? styles.hideDot : '')}
        />
      ) : null}
    </button>
  </div>
);

interface HeaderProps {
  hideOnMobile?: boolean;
}

const Header = observer(({ hideOnMobile }: HeaderProps) => {
  const { isMobile } = useIsMobile();
  const { headerBanner } = useUIStore();
  const { showOrderSummary } = useHeaderContent();
  const {
    activeSubscriptions,
    balances,
    claimablePrizesCount,
    currentUser,
    isInMonthlyRaffle,
    isOperator,
    logout,
  } = useUserStore();

  const [isChristmas] = useState(useChristmasLogos());
  const [navOpen, setNavOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(false);
  const [menuItems, setMenuItems] =
    useState<(linkItem | actionItem | subpageItem | breakItem)[]>();
  const [openSubpageMenu, setOpenSubpageMenu] = useState(false);
  const [selectedSubpage, setSelectedSubpage] = useState<{
    title: string;
    pages: linkItem[];
  }>();

  useEffect(() => {
    if (currentUser && currentUser.emailAddress) {
      setMenuItems(loggedInMenu);
    } else {
      setMenuItems(loggedOutMenu);
    }
  }, [currentUser]);

  useEffect(() => {
    const body = document.body;
    if (navOpen) {
      body.style = 'height: 100vh; overflow-y: hidden;';
    } else {
      body.style = '';
    }
  }, [navOpen]);

  const toggleNavigation = () => {
    if (navOpen) {
      setNavOpen(false);
      setTimeout(() => setNavVisible(false), NAVIGATION_CSS_TRANSITION_MILLIS);
    } else {
      setNavVisible(true);
      // Force Safari to paint the initial state before transitioning
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setNavOpen(true);
        });
      });
    }
  };

  const handleCloseExpandedMenu = () => {
    setNavOpen(false);

    setTimeout(() => {
      setOpenSubpageMenu(false);
      setNavVisible(false);
    }, NAVIGATION_CSS_TRANSITION_MILLIS);
  };

  const openSubpage = (page: string, subpages: linkItem[]) => {
    setSelectedSubpage({
      title: page,
      pages: subpages,
    });
    setOpenSubpageMenu(true);
  };

  const runAction = (action: string) => {
    switch (action) {
      case 'logout': {
        logout();
        break;
      }
    }
  };

  const DaymadeLogo = () => (
    <Link
      href="/?redirect=false"
      className={
        // ENG-1448 - This className on an anchor doesn't appear to do anything. If it is styled jsx, it will no longer
        // work in NextJS 16+
        'fullLogo'
      }
      style={{ textAlign: 'left' }}
    >
      <img
        src={`${ASSET_PREFIX}/images/daymade-logo${isChristmas ? '-xmas' : ''}${
          useHalloweenLogos() ? '-halloween' : ''
        }.png`}
        height="40"
        className={styles.logoImage}
        alt="DAYMADE logo"
      />
    </Link>
  );

  const ChooseYourPlanButton = ({ text = 'Get started' }) => (
    <RoundedButton
      href="/choose-your-plan"
      onClick={handleCloseExpandedMenu}
      rebrandDm2025
      style={{
        padding: '16px',
        fontSize: '16px',
        lineHeight: '20px',
      }}
    >
      {text}
    </RoundedButton>
  );

  return (
    <>
      <div
        className={clsx(
          'daymadeTypographyReset',
          styles.headerContainer,
          hideOnMobile ? styles.hideOnMobile : '',
        )}
      >
        <AnnouncementBanners headerBanner={headerBanner} />

        <div className={styles.background}>
          <div className={styles.content}>
            <div className={clsx(styles.barLinks, styles.desktop)}>
              <div className={styles.leftBarLinks}>
                <DaymadeLogo />
              </div>

              <div className={styles.rightBarLinks}>
                {/* MOBILE ONLY PAYMENT SUMMARY - ENTER FLOW */}
                {showOrderSummary && isMobile ? (
                  <div
                    className={clsx(
                      'daymadeTypographyReset',
                      styles.orderSummaryContainer,
                    )}
                  >
                    <HeaderOrderSummary />
                  </div>
                ) : (
                  <BurgerMenuComponent
                    menuOpen={navOpen}
                    isLoggedIn={Boolean(currentUser)}
                    claimablePrizesCount={claimablePrizesCount}
                    toggleNavigation={toggleNavigation}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* MAIN MENU */}
      <div
        className={clsx(
          styles.menu,
          navOpen && styles.menuOpen,
          !navVisible && styles.menuHidden,
          currentUser || headerBanner ? styles.menuLoggedIn : '',
        )}
      >
        <div className={styles.menuContent}>
          <div
            className={clsx(
              styles.menuHeader,
              currentUser || headerBanner ? styles.menuHeaderWithUser : '',
            )}
          >
            {currentUser && (
              <Link
                href="/account"
                className={styles.menuUser}
                onClick={handleCloseExpandedMenu}
              >
                <div className={styles.avatar}>
                  <ImageWithFallback
                    src={currentUser?.avatarLocation || ''}
                    fallbackSrc={NEW_MASCOTS[1]}
                    alt="User profile icon"
                    layout="fill"
                    objectFit="cover"
                  />
                </div>
                <div className={styles.userInfo}>
                  <div className={styles.userName}>
                    {currentUser?.firstName}
                  </div>
                  <div className={styles.userEmail}>
                    {currentUser?.emailAddress}
                  </div>
                </div>
              </Link>
            )}
            {!currentUser && headerBanner ? (
              <div className={styles.menuOpenDaymadeLogo}>
                <DaymadeLogo />
              </div>
            ) : null}
            <button
              type="button"
              aria-label="Close menu"
              className={clsx(
                styles.menuClose,
                (currentUser && currentUser.id) || headerBanner
                  ? styles.menuCloseLoggedIn
                  : '',
              )}
              onClick={handleCloseExpandedMenu}
            >
              <span
                className={clsx(styles.menuBar, styles.menuCloseOne)}
              ></span>
              <span
                className={clsx(styles.menuBar, styles.menuCloseTwo)}
              ></span>
            </button>
          </div>
          <div className={styles.menuItemsWrapper}>
            {menuItems && menuItems.length > 0 ? (
              <div className={styles.menuItems}>
                {menuItems.map((item, i) => {
                  if (isLinkItem(item)) {
                    if (item.condition === 'admin-only' && !isOperator) return;

                    return (
                      <Link href={item.link} key={i}>
                        <div
                          className={clsx(styles.menuItem)}
                          onClick={handleCloseExpandedMenu}
                        >
                          <div>{item.title}</div>
                          {item?.counter?.type === 'tokens' &&
                          balances?.tokenBalance &&
                          balances?.tokenBalance > 0 ? (
                            <div
                              className={clsx(
                                styles.counter,
                                styles[`counter--${item.counter.colour}`],
                                'aboutUsTypographyCaption',
                              )}
                            >
                              {balances?.tokenBalance}
                            </div>
                          ) : null}
                          {item?.counter?.type === 'wins' &&
                            claimablePrizesCount > 0 && (
                              <div
                                className={clsx(
                                  styles.counter,
                                  styles[`counter--${item.counter.colour}`],
                                  'aboutUsTypographyCaption',
                                )}
                              >
                                {claimablePrizesCount}
                              </div>
                            )}
                        </div>
                      </Link>
                    );
                  }

                  if (isSubpagesItem(item)) {
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openSubpage(item.title, item.subpages)}
                      >
                        <div className={styles.menuItem}>
                          {item.title}
                          <Chevron />
                        </div>
                      </button>
                    );
                  }

                  if (isActionItem(item)) {
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => runAction(item.action)}
                      >
                        <div className={styles.menuItem}>{item.title}</div>
                      </button>
                    );
                  }

                  if (isBreakItem(item)) {
                    return (
                      <div key={i} className={clsx(styles.menuBreak)}></div>
                    );
                  }
                })}
              </div>
            ) : null}
            <div
              className={clsx(
                styles.subpage,
                openSubpageMenu ? styles.subpageOpen : '',
              )}
            >
              <button type="button" onClick={() => setOpenSubpageMenu(false)}>
                <div
                  className={clsx(styles.menuItem, styles.menuItemSubpageTitle)}
                >
                  <Chevron />
                  {selectedSubpage?.title}
                </div>
              </button>
              {selectedSubpage?.pages.map((item, i) => {
                if (
                  item.condition === 'monthly-10k' &&
                  activeSubscriptions.length === 0 &&
                  !isInMonthlyRaffle
                ) {
                  return;
                }

                if (
                  item.condition === 'referrals' &&
                  activeSubscriptions.length === 0
                ) {
                  return;
                }

                if (item.condition === 'off') return;

                return (
                  <Link href={item.link} key={i}>
                    <div
                      className={clsx(styles.menuItem)}
                      onClick={handleCloseExpandedMenu}
                    >
                      <div>{item.title}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          <div className={styles.menuFooter}>
            {!currentUser && (
              <div className={styles.footerAuthButtons}>
                <ChooseYourPlanButton />
                <RoundedButton
                  href="/login"
                  onClick={handleCloseExpandedMenu}
                  rebrandDm2025
                  colorStyle="OutlineDark"
                  style={{
                    padding: '16px',
                    fontSize: '16px',
                    lineHeight: '16px',
                  }}
                >
                  Login
                </RoundedButton>
              </div>
            )}

            {currentUser && activeSubscriptions.length === 0 && (
              <div className={styles.footerEnterButton}>
                <ChooseYourPlanButton text="Enter now" />
              </div>
            )}

            <MenuAnnouncement />
          </div>
        </div>
      </div>
      <div
        onClick={handleCloseExpandedMenu}
        className={clsx(
          styles.dimmedBackground,
          navOpen ? styles.showDimmedBackground : styles.hideDimmedBackground,
        )}
      />
    </>
  );
});

export default Header;
